import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "./useProject";
import { extractNameFromAnswers } from "@/utils/submissionHelpers";

interface JotFormSubmissionDB {
  id: number;
  project_id: number;
  jotform_submission_id: string;
  jotform_form_id: string;
  code: string | null;
  email: string | null;
  ip_address: string | null;
  is_referral: boolean;
  parent_submission_id: number | null;
  referral_count: number;
  jotform_status: string | null;
  is_deleted_in_jotform: boolean;
  report_url: string | null;
  report_generated_at: string | null;
  raw_answers: Record<string, any>;
  jotform_created_at: string;
  jotform_updated_at: string | null;
  synced_at: string;
  created_at: string;
  updated_at: string;
  // Joined data
  parent?: {
    code: string | null;
    email: string | null;
    raw_answers?: Record<string, any>;
  };
  form_mapping?: {
    form_title: string | null;
    form_launch_id: number | null;
  };
}

interface JotFormSubmission {
  id: string;
  form_id: string;
  ip: string;
  created_at: string;
  status: string;
  new: string;
  flag: string;
  notes: string;
  updated_at: string;
  answers: Record<string, any>;
  mapped_answers?: any[];
  parent_submission_id?: string;
  referidoPor?: string;
  referidoNombre?: string;
  referidoEmail?: string;
  // DB-specific fields
  db_id?: number;
  code?: string;
  is_referral?: boolean;
  referral_count?: number;
  report_url?: string;
  parent?: {
    code: string | null;
    email: string | null;
    name?: string;
    jotform_submission_id?: string;
    form_id?: string;
  };
  form_mapping?: {
    form_title: string | null;
    form_launch_id: number | null;
  };
}

interface UseJotFormSubmissionsOptions {
  formId?: string;
  limit?: number;
  offset?: number;
  orderby?: string;
  direction?: 'ASC' | 'DESC';
  enabled?: boolean;
  optimized?: boolean; // Para usar consulta optimizada en vista previa
}

export function useJotFormSubmissions(options: UseJotFormSubmissionsOptions = {}) {
  const {
    formId,
    limit = 100,
    offset = 0,
    orderby = 'created_at',
    direction = 'DESC',
    enabled = true,
    optimized = false,
  } = options;

  const { projectId } = useProject();
  const queryClient = useQueryClient();

  // Mutation to sync submissions from JotForm
  const syncMutation = useMutation({
    mutationFn: async (formId: string) => {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/sync-jotform-submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`
        },
        body: JSON.stringify({
          formId,
          project_id: projectId,
          limit: 1000
        })
      });

      if (!response.ok) {
        throw new Error('Error syncing submissions');
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jotform-submissions-db', formId] });
    }
  });

  // Query to get submissions from database
  const query = useQuery({
    queryKey: ['jotform-submissions-db', formId, limit, offset, orderby, direction, projectId],
    queryFn: async () => {
      if (!formId || !projectId) {
        throw new Error('Form ID and project are required');
      }

      // Fetch from database
      const selectFields = optimized ? `
        id,
        project_id,
        jotform_submission_id,
        jotform_form_id,
        form_mapping_id,
        code,
        email,
        ip_address,
        is_referral,
        parent_submission_id,
        referral_count,
        jotform_status,
        is_deleted_in_jotform,
        report_url,
        report_generated_at,
        raw_answers,
        mapped_answers,
        parent:parent_submission_id (code, email),
        form_mapping:form_mapping_id (form_title, jotform_form_id)
      ` : `
        *,
        parent:parent_submission_id (code, email),
        form_mapping:form_mapping_id (form_title, jotform_form_id)
      `;

      const result = await (supabase as any)
        .from('jotform_submissions')
        .select(selectFields, { count: 'exact' })
        .eq('project_id', projectId)
        .eq('jotform_form_id', formId)
        .eq('is_deleted_in_jotform', false)
        .order(orderby === 'created_at' ? 'jotform_created_at' : orderby, { ascending: direction === 'ASC' })
        .range(offset, offset + limit - 1);

      if (result.error) {
        console.error('Error fetching from DB:', result.error);
        throw result.error;
      }

      const data = result.data as any[] || [];
      const count = result.count;

      // Transform DB data to match JotFormSubmission interface
      const submissions: JotFormSubmission[] = data.map((dbSub: any) => ({
        id: dbSub.jotform_submission_id,
        form_id: dbSub.jotform_form_id,
        ip: dbSub.ip_address || '',
        created_at: dbSub.jotform_created_at,
        status: dbSub.jotform_status || 'ACTIVE',
        new: '0',
        flag: '0',
        notes: '',
        updated_at: dbSub.jotform_updated_at || dbSub.jotform_created_at,
        answers: dbSub.raw_answers,
        mapped_answers: dbSub.mapped_answers,
        // DB-specific fields
        db_id: dbSub.id,
        code: dbSub.code,
        is_referral: dbSub.is_referral,
        referral_count: dbSub.referral_count,
        report_url: dbSub.report_url,
        parent_submission_id: dbSub.parent_submission_id?.toString(),
        parent: dbSub.parent
      }));

      return {
        submissions,
        resultSet: { count: count || submissions.length }
      };
    },
    enabled: enabled && !!formId && !!projectId,
    retry: 1,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  return {
    ...query,
    sync: () => formId && syncMutation.mutate(formId),
    isSyncing: syncMutation.isPending
  };
}

export function useJotFormSubmission(submissionId: string, formId: string) {
  const { projectId } = useProject();

  return useQuery({
    queryKey: ['jotform-submission-db', submissionId, formId, projectId],
    queryFn: async () => {
      if (!submissionId || !formId || !projectId) {
        throw new Error('Submission ID, Form ID, and project are required');
      }

      console.log('🔍 DEBUG - Fetching submission:', { submissionId, formId, projectId });

      // Fetch submission from database
      const query = supabase
        .from('jotform_submissions')
        .select(`
          *,
          form_mapping:form_mapping_id (form_title, jotform_form_id)
        `)
        .eq('project_id', projectId)
        .eq('jotform_submission_id', submissionId)
        .eq('is_deleted_in_jotform', false);

      console.log('🔍 DEBUG - Query filters:', { 
        project_id: projectId, 
        jotform_submission_id: submissionId,
        is_deleted_in_jotform: false
      });

      const result = await (query as any).maybeSingle();

      console.log('🔍 DEBUG - Raw result from Supabase:', {
        hasData: !!result.data,
        hasError: !!result.error,
        dataId: result.data?.id,
        dataJotformId: result.data?.jotform_submission_id
      });

      if (result.error) {
        console.error('❌ Error fetching submission from DB:', result.error);
        throw result.error;
      }

      if (!result.data) {
        console.error('❌ No submission found for:', { submissionId, formId, projectId });
        throw new Error('Submission no encontrada');
      }

      const data = result.data as any;

      console.log('🔍 DEBUG - Submission data fetched:', {
        id: data.id,
        jotform_submission_id: data.jotform_submission_id,
        parent_submission_id: data.parent_submission_id,
        is_referral: data.is_referral,
        referral_count: data.referral_count,
        email: data.email
      });

      // Fetch parent submission separately if it exists
      let parentData = null;
      if (data.parent_submission_id) {
        console.log('🔍 DEBUG - Fetching parent submission ID:', data.parent_submission_id);
        
        const parentResult = await (supabase as any)
          .from('jotform_submissions')
          .select('id, code, email, jotform_submission_id, jotform_form_id, raw_answers')
          .eq('id', data.parent_submission_id)
          .eq('project_id', projectId)
          .maybeSingle();

        if (parentResult.error) {
          console.error('Error fetching parent submission:', parentResult.error);
        } else if (parentResult.data) {
          parentData = parentResult.data;
          console.log('🔍 DEBUG - Parent submission fetched:', {
            id: parentData.id,
            code: parentData.code,
            form_id: parentData.jotform_form_id,
            has_raw_answers: !!parentData.raw_answers
          });
        }
      }

      // Extract parent name if exists
      const parentName = parentData?.raw_answers 
        ? extractNameFromAnswers(parentData.raw_answers)
        : undefined;

      console.log('🔍 DEBUG - Parent name extracted:', parentName);

      // Transform DB data to JotFormSubmission
      const submission: JotFormSubmission = {
        id: data.jotform_submission_id,
        form_id: data.jotform_form_id,
        ip: data.ip_address || '',
        created_at: data.jotform_created_at,
        status: data.jotform_status || 'ACTIVE',
        new: '0',
        flag: '0',
        notes: '',
        updated_at: data.jotform_updated_at || data.jotform_created_at,
        answers: data.raw_answers,
        mapped_answers: data.mapped_answers,
        // DB-specific fields
        db_id: data.id,
        code: data.code,
        is_referral: data.is_referral,
        referral_count: data.referral_count,
        report_url: data.report_url,
        parent_submission_id: data.parent_submission_id?.toString(),
        parent: parentData ? {
          code: parentData.code,
          email: parentData.email,
          name: parentName,
          jotform_submission_id: parentData.jotform_submission_id,
          form_id: parentData.jotform_form_id
        } : undefined,
        form_mapping: data.form_mapping
      };

      return submission;
    },
    enabled: !!submissionId && !!formId && !!projectId,
    retry: 1,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
